import { observer } from "mobx-react-lite"
import React from "react"

import {
  BinaryExpression,
  Constant,
  Formula,
  Function,
  Marker,
  Reference,
  UnaryExpression,
  Unknown,
} from "./types"
import FormulaMarker from "./FormulaMarker"

import { ParseResult } from "."

const renderBinaryExpression = (range: BinaryExpression): React.ReactNode[] => {
  return [
    ...renderFormula(range.left),
    range.token,
    ...renderFormula(range.right),
  ]
}

const renderUnaryExpression = (range: UnaryExpression): React.ReactNode[] => {
  return [range.token, ...renderFormula(range.value)]
}

export const renderFormula = (formula: Formula | Marker) => {
  if (!("type" in formula)) return [formula.token]

  switch (formula.type) {
    case "un-exp": {
      return renderUnaryExpression(formula)
    }
    case "bin-exp": {
      return renderBinaryExpression(formula)
    }
    case "func": {
      return renderFunction(formula)
    }
    case "ref": {
      return renderRef(formula)
    }
    case "const": {
      return renderConst(formula)
    }
    case "unknown": {
      return renderUnknown(formula)
    }

    default:
      return [""]
  }
}

const renderBlock = (formula: Function) => {
  const initial = []

  if (formula.open) initial.push(<FormulaMarker type="symbol">(</FormulaMarker>)

  const res = formula.arguments.reduce<React.ReactNode[]>((acc, it, index) => {
    if (index !== 0) acc.push(",")
    acc.push(renderFormula(it))
    return acc
  }, initial)

  if (formula.closed) res.push(<FormulaMarker type="symbol">)</FormulaMarker>)

  return res
}

const renderRef = (formula: Reference) => {
  return [<FormulaMarker type="ref">{formula.token}</FormulaMarker>]
}

const renderFunction = (formula: Function) => {
  return [
    <FormulaMarker type="func-name">{formula.token}</FormulaMarker>,
    renderBlock(formula),
  ]
}

const renderConst = (formula: Constant) => {
  return [<FormulaMarker type="const">{formula.token}</FormulaMarker>]
}

const renderUnknown = (formula: Unknown) => {
  return [<FormulaMarker type="unknown">{formula.token}</FormulaMarker>]
}

const HighlightedText: React.FC<{ value: string; tree?: ParseResult | null }> =
  observer(({ value, tree }) => {
    if (tree == null) return <>{value}</>
    return (
      <>
        ={renderFormula(tree.matched)}
        {renderFormula(tree.rest)}
      </>
    )
  })

export default HighlightedText
